import { z } from "zod";
import {
    DAILY_PRO_RATA_TERM_UNITS_MESSAGE,
    DEFAULT_SCHEDULE_TEMPLATE_GROUP,
    doScheduleAllocationsSumTo100,
    MAX_RECOGNITION_COMPONENTS,
    MAX_SCHEDULE_MILESTONES,
    MAX_SCHEDULE_TEMPLATE_ENTITY_ACCOUNTS,
    MAX_SCHEDULE_TEMPLATE_DESCRIPTION_LENGTH,
    MAX_SCHEDULE_TEMPLATE_GROUP_LENGTH,
    MAX_SCHEDULE_TEMPLATE_NAME_LENGTH,
    MAX_TERM_VALUE_BY_UNIT,
    SINGLE_TERM_ROW_MESSAGE,
    getTermLengthLimitMessage,
} from "@common/constants/domains/accounting/schedules";
import { hasSpecificDateAnchor } from "@common/helpers/domains/accounting/scheduleAnchors";
import {
    isDailyProRataTermUnitAllowed,
    isTermRecognitionMethod,
    RecognitionMethod,
    ExpenseSubMode,
    RevenueSubMode,
    ScheduleTermUnit,
    ScheduleType,
} from "@common/types/domains/accounting/schedule";
import {
    allocationPercentSchema,
    recognitionAnchorSchema,
    recognitionMethodSchema,
    expenseSubModeSchema,
    revenueSubModeSchema,
    scheduleTemplateMilestoneBaseSchema,
    scheduleTemplateStatusSchema,
    scheduleTermUnitSchema,
    scheduleTypeSchema,
} from "@common/schemas/domains/accounting/schedule.schema";
import { accountingLedgerBasisSchema } from "@common/schemas/domains/accounting/ledger.schema";
import {
    hasMilestoneComponent,
    milestoneExampleDateSchema,
    refineMilestoneExampleDates,
    refineMilestonePlan,
    refineScheduleMilestones,
    SCHEDULE_MILESTONE_OVERRIDE_MESSAGE,
    scheduleTemplateMilestoneInputSchema,
    scheduleTemplateMilestonesInputSchema,
} from "@common/schemas/domains/accounting/scheduleMilestone.schema";

export const scheduleRecognitionComponentInputSchema = z.object({
    method: recognitionMethodSchema,
    anchor: recognitionAnchorSchema,
    offsetDays: z.number().int().min(-3650).max(3650).default(0),
    allocationPercent: allocationPercentSchema,
});

export const SCHEDULE_ACCOUNTS_MUST_DIFFER_MESSAGE =
    "Recognition account must differ from the deferral account";

export const scheduleTemplateEntityAccountsInputSchema = z.object({
    entityId: z.number().int().positive(),
    holdingAccountId: z.string().uuid(),
    recognitionAccountId: z.string().uuid(),
});

export const scheduleRecognitionComponentsInputSchema = z
    .array(scheduleRecognitionComponentInputSchema)
    .min(1)
    .max(MAX_RECOGNITION_COMPONENTS);

const scheduleRecognitionConfigFields = {
    termLength: z.number().int().min(1),
    termUnit: scheduleTermUnitSchema,
    components: scheduleRecognitionComponentsInputSchema,
    milestones: scheduleTemplateMilestonesInputSchema,
    salvageAmount: z.number().nonnegative().nullish(),
};

const scheduleTemplatePayloadFields = {
    type: scheduleTypeSchema,
    revenueSubMode: revenueSubModeSchema.nullish(),
    expenseSubMode: expenseSubModeSchema.nullish(),
    name: z.string().trim().min(1).max(MAX_SCHEDULE_TEMPLATE_NAME_LENGTH),
    description: z
        .string()
        .trim()
        .max(MAX_SCHEDULE_TEMPLATE_DESCRIPTION_LENGTH)
        .nullish(),
    group: z
        .string()
        .trim()
        .min(1)
        .max(MAX_SCHEDULE_TEMPLATE_GROUP_LENGTH)
        .default(DEFAULT_SCHEDULE_TEMPLATE_GROUP),
    entityAccounts: z
        .array(scheduleTemplateEntityAccountsInputSchema)
        .min(1)
        .max(MAX_SCHEDULE_TEMPLATE_ENTITY_ACCOUNTS),
    ...scheduleRecognitionConfigFields,
};

interface ScheduleRecognitionConfigValue {
    type?: ScheduleType;
    termLength: number;
    termUnit: ScheduleTermUnit;
    components: Array<{
        method: RecognitionMethod;
        allocationPercent: number;
    }>;
    milestones?: Array<{ allocationPercent: number }>;
    salvageAmount?: number | null;
}

/** Rows a schedule overrides its policy with: never a milestone row, which only a policy plans. */
export function refineScheduleOverrideComponents(options: {
    components: ScheduleRecognitionComponentInput[];
    ctx: z.RefinementCtx;
    path?: string[];
}): void {
    const { components, ctx, path = ["components"] } = options;
    refineScheduleComponents(components, ctx);
    if (hasMilestoneComponent(components)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path,
            message: SCHEDULE_MILESTONE_OVERRIDE_MESSAGE,
        });
    }
}

interface ScheduleTemplatePayloadValue extends ScheduleRecognitionConfigValue {
    type: ScheduleType;
    revenueSubMode?: RevenueSubMode | null;
    expenseSubMode?: ExpenseSubMode | null;
    entityAccounts: Array<{
        entityId: number;
        holdingAccountId: string;
        recognitionAccountId: string;
    }>;
}

export function refineScheduleComponents(
    components: ScheduleRecognitionConfigValue["components"],
    ctx: z.RefinementCtx,
): void {
    const allocationPercents = components.map(
        (component) => component.allocationPercent,
    );
    if (!doScheduleAllocationsSumTo100(allocationPercents)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["components"],
            message: "Component allocations must sum to 100%",
        });
    }

    const termComponentCount = components.filter((component) =>
        isTermRecognitionMethod(component.method),
    ).length;
    if (termComponentCount > 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["components"],
            message: SINGLE_TERM_ROW_MESSAGE,
        });
    }
}

export function refineDailyProRataTermUnit(
    options: {
        components?: ReadonlyArray<{ method: RecognitionMethod }> | undefined;
        termUnit?: ScheduleTermUnit | undefined;
        termUnitPath?: string;
    },
    ctx: z.RefinementCtx,
): void {
    if (options.components === undefined || options.termUnit === undefined) {
        return;
    }
    if (
        isDailyProRataTermUnitAllowed({
            components: options.components,
            termUnit: options.termUnit,
        })
    ) {
        return;
    }
    ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [options.termUnitPath ?? "termUnit"],
        message: DAILY_PRO_RATA_TERM_UNITS_MESSAGE,
    });
}

export function refineScheduleRecognitionConfig(
    value: ScheduleRecognitionConfigValue,
    ctx: z.RefinementCtx,
): void {
    refineScheduleComponents(value.components, ctx);
    refineScheduleMilestones(value, ctx);
    refineDailyProRataTermUnit(value, ctx);

    if (value.termLength > MAX_TERM_VALUE_BY_UNIT[value.termUnit]) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["termLength"],
            message: getTermLengthLimitMessage(value.termUnit),
        });
    }

    if (value.salvageAmount != null && value.type === ScheduleType.revenue) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["salvageAmount"],
            message: "Salvage amount is not allowed on revenue schedules",
        });
    }
}

function refineSubModeMatchesType(options: {
    ctx: z.RefinementCtx;
    type: ScheduleType;
    subMode: RevenueSubMode | ExpenseSubMode | null | undefined;
    subModeType: ScheduleType;
    field: "revenueSubMode" | "expenseSubMode";
    label: "Revenue" | "Expense";
}): void {
    const { ctx, type, subMode, subModeType, field, label } = options;
    const isSubModeType = type === subModeType;
    if (isSubModeType && subMode == null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [field],
            message: `${label} schedules require a sub-mode`,
        });
    }
    if (!isSubModeType && subMode != null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [field],
            message: `${label} sub-mode is only allowed on ${label.toLowerCase()} schedules`,
        });
    }
}

export function refineScheduleTemplatePayload(
    value: ScheduleTemplatePayloadValue,
    ctx: z.RefinementCtx,
): void {
    refineScheduleRecognitionConfig(value, ctx);

    refineSubModeMatchesType({
        ctx,
        type: value.type,
        subMode: value.revenueSubMode,
        subModeType: ScheduleType.revenue,
        field: "revenueSubMode",
        label: "Revenue",
    });
    refineSubModeMatchesType({
        ctx,
        type: value.type,
        subMode: value.expenseSubMode,
        subModeType: ScheduleType.expense,
        field: "expenseSubMode",
        label: "Expense",
    });

    const seenEntityIds = new Set<number>();
    for (const [index, accountRow] of value.entityAccounts.entries()) {
        if (seenEntityIds.has(accountRow.entityId)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["entityAccounts", index, "entityId"],
                message: "Each entity can only appear once",
            });
        }
        seenEntityIds.add(accountRow.entityId);

        if (accountRow.holdingAccountId === accountRow.recognitionAccountId) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["entityAccounts", index, "recognitionAccountId"],
                message: SCHEDULE_ACCOUNTS_MUST_DIFFER_MESSAGE,
            });
        }
    }
}

export const createScheduleTemplateBodySchema = z
    .object(scheduleTemplatePayloadFields)
    .superRefine(refineScheduleTemplatePayload);

export const updateScheduleTemplateBodySchema = z
    .object(scheduleTemplatePayloadFields)
    .superRefine(refineScheduleTemplatePayload);

export type CreateScheduleTemplateBody = z.infer<
    typeof createScheduleTemplateBodySchema
>;
export type CreateScheduleTemplateBodyInput = z.input<
    typeof createScheduleTemplateBodySchema
>;
export type UpdateScheduleTemplateBody = z.infer<
    typeof updateScheduleTemplateBodySchema
>;

export const previewScheduleRecognitionBodySchema = z
    .object({
        ...scheduleRecognitionConfigFields,
        amount: z.number().positive(),
        termStart: z.string().date(),
        sourceDate: z.string().date().optional(),
        specificDate: z.string().date().nullish(),
        milestoneExampleDates: z
            .array(milestoneExampleDateSchema)
            .max(MAX_SCHEDULE_MILESTONES)
            .optional(),
    })
    .superRefine((value, ctx) => {
        refineScheduleRecognitionConfig(value, ctx);
        refineMilestoneExampleDates(value, ctx);
        if (value.salvageAmount != null && value.salvageAmount > value.amount) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["salvageAmount"],
                message: "Salvage amount must not exceed the amount",
            });
        }
        if (
            hasSpecificDateAnchor(value.components) &&
            value.specificDate == null
        ) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["specificDate"],
                message:
                    "A specific date is required when a component anchors on a specific date",
            });
        }
    });

export type PreviewScheduleRecognitionBody = z.infer<
    typeof previewScheduleRecognitionBodySchema
>;

export const scheduleTemplateComponentSchema = z.object({
    id: z.string().uuid(),
    order: z.number().int(),
    method: recognitionMethodSchema,
    anchor: recognitionAnchorSchema,
    offsetDays: z.number().int(),
    allocationPercent: z.number(),
});

export const scheduleTemplateMilestoneSchema =
    scheduleTemplateMilestoneBaseSchema.pick({
        id: true,
        sortOrder: true,
        name: true,
        allocationPercent: true,
        anchor: true,
        offsetCount: true,
        offsetUnit: true,
        specificDate: true,
    });

export type ScheduleTemplateMilestone = z.infer<
    typeof scheduleTemplateMilestoneSchema
>;

export const scheduleTemplateAccountRowSchema = z.object({
    id: z.string().uuid(),
    entityId: z.number().int(),
    basis: accountingLedgerBasisSchema,
    holdingAccountId: z.string().uuid(),
    recognitionAccountId: z.string().uuid(),
});

export const scheduleTemplateSchema = z.object({
    id: z.string().uuid(),
    workspaceId: z.string().uuid(),
    type: scheduleTypeSchema,
    revenueSubMode: revenueSubModeSchema.nullable(),
    expenseSubMode: expenseSubModeSchema.nullable(),
    name: z.string(),
    description: z.string().nullable(),
    group: z.string(),
    termLength: z.number().int(),
    termUnit: scheduleTermUnitSchema,
    salvageAmount: z.number().nullable(),
    status: scheduleTemplateStatusSchema,
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    components: z.array(scheduleTemplateComponentSchema),
    milestones: z.array(scheduleTemplateMilestoneSchema),
    accountRows: z.array(scheduleTemplateAccountRowSchema),
});

export type ScheduleTemplateResponse = z.infer<typeof scheduleTemplateSchema>;

export const scheduleTemplateListAccountRowSchema = z.object({
    entityId: z.number().int(),
    basis: accountingLedgerBasisSchema,
    holdingAccountId: z.string().uuid(),
});

export const scheduleTemplateListItemSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    group: z.string(),
    type: scheduleTypeSchema,
    revenueSubMode: revenueSubModeSchema.nullable(),
    expenseSubMode: expenseSubModeSchema.nullable(),
    termLength: z.number().int(),
    termUnit: scheduleTermUnitSchema,
    status: scheduleTemplateStatusSchema,
    entityIds: z.array(z.number().int()),
    accountRows: z.array(scheduleTemplateListAccountRowSchema).default([]),
    journalEntryCount: z.number().int().nonnegative(),
    // Distinct anchors across the template's components. Apply surfaces read
    // these before applying: `hasSpecificDateAnchor` decides whether to collect
    // a recognition start date, and `anchors` lets a picker hide templates the
    // source cannot satisfy (a billDate anchor on a revenue schedule).
    anchors: z.array(recognitionAnchorSchema),
    hasSpecificDateAnchor: z.boolean(),
    /** Recognizes by milestone; pickers hide it while the milestone flag is off. */
    isMilestone: z.boolean(),
    updatedAt: z.coerce.date(),
});

export type ScheduleTemplateListItem = z.infer<
    typeof scheduleTemplateListItemSchema
>;

export const previewScheduleRecognitionRowSchema = z.object({
    periodKey: z.string(),
    effectiveDate: z.string().date(),
    amount: z.number(),
    componentOrder: z.number().int(),
    method: recognitionMethodSchema,
});

export const previewScheduleRecognitionResponseSchema = z.object({
    rows: z.array(previewScheduleRecognitionRowSchema),
    totalAmount: z.number(),
});

export type PreviewScheduleRecognitionResponse = z.infer<
    typeof previewScheduleRecognitionResponseSchema
>;

export const listScheduleTemplatesQuerySchema = z.object({
    includeArchived: z
        .enum(["true", "false"])
        .optional()
        .transform((value) => value === "true"),
});

export type ListScheduleTemplatesQuery = z.infer<
    typeof listScheduleTemplatesQuerySchema
>;

/** A term override or change sets its length and unit together, within the unit's maximum. */
export function addTermPairingIssues(
    ctx: z.RefinementCtx,
    term: { termLength?: number; termUnit?: ScheduleTermUnit },
): void {
    if ((term.termLength === undefined) !== (term.termUnit === undefined)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["termLength"],
            message: "Term length and unit must be set together",
        });
        return;
    }
    if (
        term.termLength !== undefined &&
        term.termUnit !== undefined &&
        term.termLength > MAX_TERM_VALUE_BY_UNIT[term.termUnit]
    ) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["termLength"],
            message: getTermLengthLimitMessage(term.termUnit),
        });
    }
}

/**
 * What a schedule may differ from its policy in. Rows or accounts that differ
 * turn the schedule into a one-off; a term alone is stored on the schedule.
 * `milestones` is only for a milestone policy, which refuses the other
 * structure overrides; a calendar policy refuses `milestones`.
 */
export const scheduleRecognitionOverridesSchema = z
    .object({
        components: scheduleRecognitionComponentsInputSchema.optional(),
        milestones: z
            .array(scheduleTemplateMilestoneInputSchema)
            .max(MAX_SCHEDULE_MILESTONES)
            .optional(),
        holdingAccountId: z.string().uuid().optional(),
        recognitionAccountId: z.string().uuid().optional(),
        termLength: z.number().int().min(1).optional(),
        termUnit: scheduleTermUnitSchema.optional(),
    })
    .superRefine((overrides, ctx) => {
        if (overrides.components) {
            refineScheduleOverrideComponents({
                components: overrides.components,
                ctx,
            });
        }
        if (overrides.milestones) {
            refineMilestonePlan(overrides.milestones, ctx);
        }
        addTermPairingIssues(ctx, overrides);
        refineDailyProRataTermUnit(overrides, ctx);
    });

export type ScheduleRecognitionOverrides = z.infer<
    typeof scheduleRecognitionOverridesSchema
>;

export type ScheduleRecognitionComponentInput = z.infer<
    typeof scheduleRecognitionComponentInputSchema
>;
